/**
 * A small drawing of a paper Ordo prints, with the columns a photo of it fills
 * picked out, and the fields it fills listed UNDER it (PROD-1511: one column,
 * never side by side). Every upload that asks for a paper shows this, so the
 * person holding three papers can tell which one goes where.
 *
 * Three papers:
 *   - `delivery`: the chef's Orders sheet as a school sees it (Drop-off °F,
 *     Received and the drop-off sign-off are theirs; Kitchen °F is the chef's);
 *   - `record`: the production record's PREP COPY (the service columns and the
 *     service sign-off);
 *   - `chef`: the same Orders sheet as the chef sees it (Kitchen °F and
 *     Drop-off °F, the two columns the Temperature logs read).
 *
 * The lists say what the reader actually fills, including the overlap: a PREP
 * COPY still carries Received and Received °F, and they apply only where the
 * delivery sheet left them blank (the flow reads the record first, then the
 * Orders sheet).
 *
 * Presentation only; decorative to assistive tech except the list.
 */

import { cn } from '@ordo/ui/cn'

export type SheetPreviewPaper = 'delivery' | 'record' | 'chef'

/** What a photo of each paper fills, in the order the lists print them. */
export const SHEET_FILLS: Record<SheetPreviewPaper, readonly string[]> = {
  delivery: ['Received', 'Received °F', 'Received by, time'],
  record: [
    'Served, Reimb., Non-reimb., Leftover',
    'Serving °F',
    'Service time',
    'Received and Received °F, if the delivery sheet left them blank',
  ],
  chef: ['Kitchen °F', 'Drop-off °F'],
}

/** A picked-out column: solid gray, never the primary blue, so the drawing never reads as a button. */
const HL = 'rounded-[3px] bg-foreground/[0.07] font-semibold text-foreground/80 ring-1 ring-foreground/35'
const LINE = 'h-[3px] rounded-full bg-muted-foreground/25'
const BOX = 'h-3 rounded-[2px] border border-dashed border-muted-foreground/40'

interface Column {
  label: string
  highlighted: boolean
}

const COLUMNS: Record<SheetPreviewPaper, readonly Column[]> = {
  delivery: [
    { label: 'Kitchen °F', highlighted: false },
    { label: 'Drop-off °F', highlighted: true },
    { label: 'Received', highlighted: true },
  ],
  record: [
    { label: 'Served', highlighted: true },
    { label: 'Reimb.', highlighted: true },
    { label: 'Non-r.', highlighted: true },
    { label: 'Left', highlighted: true },
    { label: 'Srv °F', highlighted: true },
  ],
  chef: [
    { label: 'Kitchen °F', highlighted: true },
    { label: 'Drop-off °F', highlighted: true },
    { label: 'Received', highlighted: false },
  ],
}

const FOOTER: Record<SheetPreviewPaper, { label: string; highlighted: boolean }> = {
  delivery: { label: 'Drop-off sign-off', highlighted: true },
  record: { label: 'Service signature, time', highlighted: true },
  chef: { label: 'Kitchen sign-out · Drop-off sign-off', highlighted: false },
}

/** The drawing alone: a few rows of the paper, its picked-out columns, and its sign-off strip. */
export function SheetDrawing({ paper, className }: { paper: SheetPreviewPaper; className?: string }) {
  const columns = COLUMNS[paper]
  const template = { gridTemplateColumns: `minmax(0,1fr) repeat(${columns.length}, minmax(2.5rem, auto))` }
  const footer = FOOTER[paper]
  return (
    <div
      aria-hidden="true"
      data-sheet-drawing={paper}
      className={cn(
        'flex w-full flex-col gap-1.5 rounded-lg border bg-background p-2.5 text-[9.5px] leading-3 text-muted-foreground',
        className,
      )}>
      {paper === 'record' ? (
        <span className="self-start rounded-[3px] border border-foreground px-1.5 py-px font-bold text-foreground">
          PREP COPY
        </span>
      ) : (
        <span className="h-[5px] w-28 rounded-full bg-muted-foreground/45" />
      )}
      <div className="grid gap-1" style={template}>
        <span>Item</span>
        {columns.map((column) => (
          <span key={column.label} className={cn('px-0.5 text-center', column.highlighted && HL)}>
            {column.label}
          </span>
        ))}
      </div>
      {[0, 1, 2].map((line) => (
        <div key={line} className="grid items-center gap-1" style={template}>
          <span className={LINE} />
          {columns.map((column) => (
            <span key={column.label} className={column.highlighted ? cn('h-3', HL) : BOX} />
          ))}
        </div>
      ))}
      <span
        className={cn(
          'flex h-[22px] items-center px-1.5',
          footer.highlighted ? HL : 'rounded-[3px] border border-muted-foreground/30',
        )}>
        {footer.label}
      </span>
    </div>
  )
}

/** The drawing, then what a photo of it fills, one column. */
export function SheetPreview({ paper, className }: { paper: SheetPreviewPaper; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <SheetDrawing paper={paper} />
      <div className="flex flex-col gap-1 text-[13px] leading-[19px]">
        <span className="text-muted-foreground">Fills in</span>
        <ul className="flex flex-col gap-1" aria-label="Fills in">
          {SHEET_FILLS[paper].map((field) => (
            <li key={field}>{field}</li>
          ))}
        </ul>
      </div>
    </div>
  )
}
